import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, TextInput, StyleSheet } from 'react-native';
import LoadingSpinner from './LoadingSpinner';

/**
 * Cache Data Explorer
 * 
 * Interactive browser for cached odds data:
 * - Search by team, league, or game
 * - Browse by league → fixture → market → odds
 * - Expandable drill-down interface
 * - Real-time data visualization
 */
const CacheDataExplorer = ({ functionsUrl }) => {
  const [searchQuery, setSearchQuery] = useState(''); // Search teams
  const [playerSearch, setPlayerSearch] = useState(''); // ⭐ NEW: Search players
  const [marketSearch, setMarketSearch] = useState(''); // ⭐ NEW: Search markets
  const [selectedLeague, setSelectedLeague] = useState('all');
  const [fixtures, setFixtures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedFixtures, setExpandedFixtures] = useState({});
  const [expandedMarkets, setExpandedMarkets] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [loadingMarkets, setLoadingMarkets] = useState({}); // Track which markets are loading
  const [fullMarketData, setFullMarketData] = useState({}); // Store loaded comprehensive market data
  const [expandedMath, setExpandedMath] = useState({}); // Track which odds show math breakdown
  const [oddsDisplayLimit, setOddsDisplayLimit] = useState({}); // Track how many odds to show per market (default 200)
  
  // ⭐ Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [fixturesPerPage] = useState(25); // Show 25 fixtures per page for faster loading
  const [totalFixtures, setTotalFixtures] = useState(0);
  
  // ⭐ Available leagues with data
  const [availableLeagues, setAvailableLeagues] = useState([]);
  
  // Filters
  const [filterSportsbooks, setFilterSportsbooks] = useState([]);
  const [filterMarkets, setFilterMarkets] = useState([]);
  const [sortBy, setSortBy] = useState('ev'); // 'ev', 'date', 'price_desc', 'price_asc'
  const [marketSearchQuery, setMarketSearchQuery] = useState(''); // Search within market filters
  
  // Available leagues
  const leagues = [
    { id: 'all', name: 'All Leagues', sport: '' },
    { id: 'nba', name: 'NBA', sport: 'basketball' },
    { id: 'wnba', name: 'WNBA', sport: 'basketball' },
    { id: 'nfl', name: 'NFL', sport: 'football' },
    { id: 'nhl', name: 'NHL', sport: 'hockey' },
    { id: 'ncaab', name: 'NCAAB', sport: 'basketball' },
    { id: 'ncaaf', name: 'NCAAF', sport: 'football' },
    { id: 'mlb', name: 'MLB', sport: 'baseball' },
    { id: 'mls', name: 'MLS', sport: 'soccer' },
    { id: 'epl', name: 'EPL', sport: 'soccer' },
    { id: 'atp', name: 'ATP Tennis', sport: 'tennis' }, // ⭐ ADD: ATP Tennis
    { id: 'wta', name: 'WTA Tennis', sport: 'tennis' }, // ⭐ ADD: WTA Tennis
  ];
  
  // Fetch fixtures for selected league (with full odds data included)
  const fetchFixtures = async (league = 'all', page = 1) => {
    try {
      setLoading(true);
      
      // Calculate offset for pagination
      const offset = (page - 1) * fixturesPerPage;
      
      const params = new URLSearchParams();
      if (league !== 'all') {
        params.append('league', league);
      }
      params.append('limit', fixturesPerPage.toString());
      
      // Always use odds_cache endpoint (unified view)
      const endpoint = `${functionsUrl}/getAllCachedFixtures`;
        params.append('offset', offset.toString()); // ⭐ Add offset for pagination
        params.append('include_odds', 'true'); // ⭐ Include full odds data in response
      
      const response = await fetch(`${endpoint}?${params}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      console.log('[CacheExplorer] Fetched data:', {
        success: data.success,
        source: 'odds_cache',
        league: league,
        fixtures_count: data.fixtures?.length || 0,
        total_available: data.total_available || data.count || 0,
        page: page,
        sample: data.fixtures?.[0]
      });
      
      if (data.success) {
        setFixtures(data.fixtures || []);
        setTotalFixtures(data.total_available || data.count || data.fixtures?.length || 0);
        
        // ⭐ Extract unique leagues from fixtures for filter
        if (league === 'all' && data.fixtures) {
          const leaguesInData = new Set(data.fixtures.map(f => f.league).filter(Boolean));
          setAvailableLeagues(Array.from(leaguesInData));
        }
      } else {
        console.error('[CacheExplorer] Error:', data.error);
        setFixtures([]);
        setTotalFixtures(0);
      }
    } catch (error) {
      console.error('[CacheExplorer] Fetch error:', error);
      setFixtures([]);
      setTotalFixtures(0);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch available leagues (leagues that have cached data)
  const fetchAvailableLeagues = async () => {
    try {
      // Fetch more fixtures to ensure we capture all leagues
      // NFL/NHL/WTA might be further down the list
      const response = await fetch(
        `${functionsUrl}/getAllCachedFixtures?limit=1000&include_odds=false`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.fixtures) {
        const leaguesInData = new Set(data.fixtures.map(f => f.league).filter(Boolean));
        setAvailableLeagues(['all', ...Array.from(leaguesInData)]);
        console.log('[CacheExplorer] Available leagues found:', Array.from(leaguesInData));
        console.log('[CacheExplorer] Total fixtures scanned:', data.fixtures.length);
      }
    } catch (error) {
      console.error('[CacheExplorer] Error fetching available leagues:', error);
      // Fallback: show all possible leagues
      setAvailableLeagues(['all', 'nba', 'ncaab', 'ncaaf', 'nfl', 'nhl', 'mlb', 'mls', 'atp', 'wta']);
    }
  };
  
  // Toggle fixture expansion (no API call needed - data already loaded!)
  const toggleFixture = (fixture) => {
    const docId = fixture.id;
    setExpandedFixtures(prev => ({
      ...prev,
      [docId]: !prev[docId]
    }));
  };
  
  // Load full market data from comprehensive storage
  const loadFullMarketData = async (fixtureId, marketKey) => {
    const cacheKey = `${fixtureId}_${marketKey}`;
    
    // Already loaded or loading
    if (fullMarketData[cacheKey] || loadingMarkets[cacheKey]) {
      return;
    }
    
    try {
      setLoadingMarkets(prev => ({ ...prev, [cacheKey]: true }));
      
      const response = await fetch(
        `${functionsUrl}/getMarketOdds?fixture_id=${fixtureId}&market_key=${marketKey}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        }
      );
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success) {
        setFullMarketData(prev => ({
          ...prev,
          [cacheKey]: data.odds || []
        }));
      }
    } catch (error) {
      console.error('[CacheExplorer] Error loading full market:', error);
    } finally {
      setLoadingMarkets(prev => ({ ...prev, [cacheKey]: false }));
    }
  };
  
  // Toggle market expansion
  const toggleMarket = async (fixtureId, marketKey, fixture) => {
    console.log('[CacheExplorer] toggleMarket called:', {
      fixtureId,
      marketKey,
      fixture: fixture ? `${fixture.home_team} vs ${fixture.away_team}` : 'undefined'
    });
    
    const key = `${fixtureId}_${marketKey}`;
    const isExpanding = !expandedMarkets[key];
    
    console.log('[CacheExplorer] Market toggle:', {
      key,
      isExpanding,
      currentlyExpanded: expandedMarkets[key]
    });
    
    setExpandedMarkets(prev => ({
      ...prev,
      [key]: isExpanding
    }));
    
    // If expanding and market has more data available, load it
    if (isExpanding) {
      const market = fixture.featured_markets?.[marketKey];
      if (market && market.total_available > market.odds_count) {
        // This market has more data in comprehensive storage
        // Use fixtureId parameter (which is now docId = fixture.id)
        await loadFullMarketData(fixtureId, marketKey);
      }
    }
  };
  
  // Extract available sportsbooks from all fixtures
  const availableSportsbooks = React.useMemo(() => {
    const books = new Set();
    fixtures.forEach(fixture => {
      const markets = fixture.featured_markets || fixture.markets || {};
      Object.values(markets).forEach(market => {
        (market.data || []).forEach(odd => {
          if (odd.sportsbook) books.add(odd.sportsbook);
        });
      });
    });
    return Array.from(books).sort();
  }, [fixtures]);
  
  // Extract available market types from all fixtures (from markets_summary or featured_markets)
  const availableMarkets = React.useMemo(() => {
    const markets = new Set();
    fixtures.forEach(fixture => {
      // Use markets_summary if available (comprehensive list)
      if (fixture.markets_summary) {
        fixture.markets_summary.forEach(summary => {
          if (summary.market_name) markets.add(summary.market_name);
        });
      } else {
        // Fallback to extracting from featured_markets or legacy markets
        const fixtureMarkets = fixture.featured_markets || fixture.markets || {};
        Object.values(fixtureMarkets).forEach(market => {
          if (market.market_name) markets.add(market.market_name);
        });
      }
    });
    return Array.from(markets).sort();
  }, [fixtures]);
  
  // Filter available markets based on search query
  const filteredAvailableMarkets = React.useMemo(() => {
    if (!marketSearchQuery) return availableMarkets;
    const query = marketSearchQuery.toLowerCase();
    return availableMarkets.filter(market => 
      market.toLowerCase().includes(query)
    );
  }, [availableMarkets, marketSearchQuery]);
  
  // Toggle sportsbook filter
  const toggleSportsbookFilter = (book) => {
    setFilterSportsbooks(prev => 
      prev.includes(book) ? prev.filter(b => b !== book) : [...prev, book]
    );
  };
  
  // Toggle market filter
  const toggleMarketFilter = (market) => {
    setFilterMarkets(prev => 
      prev.includes(market) ? prev.filter(m => m !== market) : [...prev, market]
    );
  };
  
  // Clear all filters
  const clearAllFilters = () => {
    setFilterSportsbooks([]);
    setFilterMarkets([]);
    setSortBy('ev');
    setSearchQuery('');
  };
  
  // Determine game status based on start_date
  const getGameStatus = (startDate) => {
    if (!startDate) return { status: 'upcoming', flag: '🔵', label: 'Upcoming' };
    
    try {
      const gameTime = startDate._seconds 
        ? new Date(startDate._seconds * 1000) 
        : new Date(startDate);
      const now = new Date();
      const diffMs = gameTime - now;
      const diffHours = diffMs / (1000 * 60 * 60);
      
      // Game started more than 3 hours ago = past
      if (diffHours < -3) {
        return { status: 'past', flag: '⚪', label: 'Final', color: '#4B5563' };
      }
      // Game is live or recently started (within last 3 hours)
      else if (diffHours < 0) {
        return { status: 'live', flag: '🟢', label: 'LIVE', color: '#10B981' };
      }
      // Game starts within next 2 hours = live soon
      else if (diffHours < 2) {
        return { status: 'live_soon', flag: '🟠', label: 'Starting Soon', color: '#F59E0B' };
      }
      // Upcoming
      else {
        return { status: 'upcoming', flag: '🔵', label: 'Upcoming', color: '#3B82F6' };
      }
    } catch (e) {
      return { status: 'upcoming', flag: '🔵', label: 'Upcoming', color: '#3B82F6' };
    }
  };

  // Group odds by selection within a market
  const groupOddsBySelection = (odds) => {
    const grouped = {};
    (odds || []).forEach(odd => {
      const key = `${odd.selection}_${odd.selection_line || 'null'}`;
      if (!grouped[key]) {
        grouped[key] = {
          selection: odd.selection,
          selection_line: odd.selection_line,
          player_id: odd.player_id,
          sportsbooks: []
        };
      }
      grouped[key].sportsbooks.push({
        sportsbook: odd.sportsbook,
        price: odd.price,
        ev_percent: odd.ev_percent,
        decimal_odds: odd.decimal_odds,
        implied_prob: odd.implied_prob
      });
    });
    
    // Sort sportsbooks by EV within each selection
    Object.values(grouped).forEach(group => {
      group.sportsbooks.sort((a, b) => (b.ev_percent || 0) - (a.ev_percent || 0));
    });
    
    return Object.values(grouped);
  };
  
  // ⭐ NEW: Filter market data to show only specific player's odds
  const filterMarketDataByPlayer = (marketData, playerQuery) => {
    if (!playerQuery || !marketData?.data) return marketData;
    
    const query = playerQuery.toLowerCase();
    const filteredData = marketData.data.filter(odd => 
      (odd.selection || '').toLowerCase().includes(query) ||
      (odd.name || '').toLowerCase().includes(query)
    );
    
    if (filteredData.length === 0) return null; // No matching odds
    
    return {
      ...marketData,
      data: filteredData,
      odds_count: filteredData.length
    };
  };
  
  // Apply filters to fixtures
  const filteredFixtures = fixtures.filter(fixture => {
    // Team search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const homeTeam = (fixture.home_team || '').toLowerCase();
      const awayTeam = (fixture.away_team || '').toLowerCase();
      const league = (fixture.league || '').toLowerCase();
      
      if (!homeTeam.includes(query) && !awayTeam.includes(query) && !league.includes(query)) {
        return false;
      }
    }
    
    // ⭐ NEW: Player search filter
    if (playerSearch) {
      const query = playerSearch.toLowerCase();
      const markets = fixture.featured_markets || fixture.markets || {};
      let hasPlayer = false;
      Object.values(markets).forEach(market => {
        (market.data || []).forEach(odd => {
          if ((odd.selection || '').toLowerCase().includes(query) ||
              (odd.name || '').toLowerCase().includes(query)) {
            hasPlayer = true;
          }
        });
      });
      if (!hasPlayer) return false;
    }
    
    // ⭐ NEW: Market search filter
    if (marketSearch) {
      const query = marketSearch.toLowerCase();
      const markets = fixture.featured_markets || fixture.markets || {};
      const hasMarket = Object.values(markets).some(market => 
        (market.market_name || '').toLowerCase().includes(query)
      );
      if (!hasMarket) return false;
    }
    
    // Sportsbook filter - check if fixture has ANY odds from selected sportsbooks
    const fixtureMarkets = fixture.featured_markets || fixture.markets || {};
    if (filterSportsbooks.length > 0 && Object.keys(fixtureMarkets).length > 0) {
      let hasMatchingSportsbook = false;
      Object.values(fixtureMarkets).forEach(market => {
        (market.data || []).forEach(odd => {
          if (filterSportsbooks.includes(odd.sportsbook)) {
            hasMatchingSportsbook = true;
          }
        });
      });
      if (!hasMatchingSportsbook) return false;
    }
    
    // Market filter - check if fixture has any of the selected markets
    if (filterMarkets.length > 0 && Object.keys(fixtureMarkets).length > 0) {
      const marketNames = Object.values(fixtureMarkets).map(m => m.market_name);
      const hasMatchingMarket = filterMarkets.some(fm => marketNames.includes(fm));
      if (!hasMatchingMarket) return false;
    }
    
    return true;
  }).sort((a, b) => {
    // Sort by: LIVE → Upcoming (soonest first) → Past (most recent first)
    const now = new Date();
    
    const getTimestamp = (fixture) => {
      if (!fixture.start_date) return 0;
      if (fixture.start_date._seconds) return fixture.start_date._seconds * 1000;
      return new Date(fixture.start_date).getTime();
    };
    
    const aTime = getTimestamp(a);
    const bTime = getTimestamp(b);
    const aStatus = getGameStatus(a.start_date);
    const bStatus = getGameStatus(b.start_date);
    
    // Priority 1: LIVE games first
    if (aStatus.status === 'live' && bStatus.status !== 'live') return -1;
    if (bStatus.status === 'live' && aStatus.status !== 'live') return 1;
    
    // Priority 2: Starting soon games
    if (aStatus.status === 'live_soon' && bStatus.status !== 'live_soon' && bStatus.status !== 'live') return -1;
    if (bStatus.status === 'live_soon' && aStatus.status !== 'live_soon' && aStatus.status !== 'live') return 1;
    
    // Priority 3: Upcoming games (soonest first)
    if (aStatus.status === 'upcoming' && bStatus.status === 'upcoming') {
      return aTime - bTime; // Ascending (soonest first)
    }
    
    // Priority 4: Past games (most recent first)
    if (aStatus.status === 'past' && bStatus.status === 'past') {
      return bTime - aTime; // Descending (most recent first)
    }
    
    // Mixed: upcoming before past
    if (aStatus.status === 'upcoming' && bStatus.status === 'past') return -1;
    if (aStatus.status === 'past' && bStatus.status === 'upcoming') return 1;
    
    // Default: by time
    return aTime - bTime;
  });
  
  // Fetch available leagues on mount
  useEffect(() => {
    fetchAvailableLeagues();
  }, []);
  
  // Load fixtures when league or page changes
  useEffect(() => {
    fetchFixtures(selectedLeague, currentPage);
  }, [selectedLeague, currentPage]);
  
  // Format date
  const formatDate = (dateObj) => {
    if (!dateObj) return 'Unknown';
    
    try {
      let date;
      if (dateObj._seconds) {
        date = new Date(dateObj._seconds * 1000);
      } else if (typeof dateObj === 'string') {
        date = new Date(dateObj);
      } else {
        date = new Date(dateObj);
      }
      
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return 'Invalid date';
    }
  };
  
  return (
    <View style={styles.container}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerTitle}>📊 Browse Cached Odds</Text>
        <Text style={styles.headerSubtitle}>
          Explore all cached data by league, team, and market
        </Text>
      </View>
      
      {/* Search Bars */}
      <View style={styles.searchContainer}>
        <TextInput
          style={styles.searchInput}
          placeholder="Search by team name or league..."
          placeholderTextColor="#999"
          value={searchQuery}
          onChangeText={setSearchQuery}
        />
      </View>
      
      {/* ⭐ NEW: Player & Market Search */}
      <View style={styles.advancedSearchContainer}>
        <TextInput
          style={styles.advancedSearchInput}
          placeholder="🏀 Search by player name..."
          placeholderTextColor="#999"
          value={playerSearch}
          onChangeText={setPlayerSearch}
        />
        <TextInput
          style={styles.advancedSearchInput}
          placeholder="📊 Search by market name..."
          placeholderTextColor="#999"
          value={marketSearch}
          onChangeText={setMarketSearch}
        />
      </View>
      
      {/* League Filter */}
      <View style={styles.leagueFilterContainer}>
        <ScrollView 
          horizontal 
          showsHorizontalScrollIndicator={false} 
          style={styles.leagueFilter}
          contentContainerStyle={styles.leagueFilterContent}
        >
          {leagues
            .filter(league => {
              // Always show "All Leagues"
              if (league.id === 'all') return true;
              // Show league if it's selected OR if it has data
              return selectedLeague === league.id || availableLeagues.includes(league.id);
            })
            .map(league => (
            <TouchableOpacity
              key={league.id}
              style={[
                styles.leagueButton,
                selectedLeague === league.id && styles.leagueButtonActive
              ]}
              onPress={() => {
                setSelectedLeague(league.id);
                setCurrentPage(1); // ⭐ Reset to page 1 when changing league
              }}
            >
              <Text style={[
                styles.leagueButtonText,
                selectedLeague === league.id && styles.leagueButtonTextActive
              ]}>
                {league.name}
              </Text>
            </TouchableOpacity>
          ))}
        </ScrollView>
      </View>
      
      {/* Filters Toggle Button */}
      <TouchableOpacity 
        style={styles.filtersToggle}
        onPress={() => setShowFilters(!showFilters)}
      >
        <Text style={styles.filtersToggleText}>
          🔍 Filters {(filterSportsbooks.length + filterMarkets.length) > 0 && `(${filterSportsbooks.length + filterMarkets.length})`}
        </Text>
        <Text style={styles.filtersToggleIcon}>{showFilters ? '▼' : '▶'}</Text>
      </TouchableOpacity>
      
      {/* Advanced Filters Panel */}
      {showFilters && (
        <View style={styles.filtersPanel}>
          {/* Sportsbook Filter */}
          <View style={styles.filterSection}>
            <View style={styles.filterSectionHeader}>
              <Text style={styles.filterLabel}>Sportsbook</Text>
              {filterSportsbooks.length > 0 && (
                <TouchableOpacity onPress={() => setFilterSportsbooks([])}>
                  <Text style={styles.clearButton}>Clear ({filterSportsbooks.length})</Text>
                </TouchableOpacity>
              )}
            </View>
            <View style={styles.filterTagsContainer}>
              {availableSportsbooks.length > 0 ? availableSportsbooks.map(book => (
                <TouchableOpacity
                  key={book}
                  onPress={() => toggleSportsbookFilter(book)}
                  style={[
                    styles.filterTag,
                    filterSportsbooks.includes(book) && styles.filterTagActive
                  ]}
                >
                  <Text style={[
                    styles.filterTagText,
                    filterSportsbooks.includes(book) && styles.filterTagTextActive
                  ]}>
                    {book}
                  </Text>
                </TouchableOpacity>
              )) : <Text style={styles.noFiltersText}>No sportsbooks</Text>}
            </View>
          </View>
          
          {/* Market Filter */}
          <View style={styles.filterSection}>
            <View style={styles.filterSectionHeader}>
              <Text style={styles.filterLabel}>Market Type ({availableMarkets.length} total)</Text>
              {filterMarkets.length > 0 && (
                <TouchableOpacity onPress={() => setFilterMarkets([])}>
                  <Text style={styles.clearButton}>Clear ({filterMarkets.length})</Text>
                </TouchableOpacity>
              )}
            </View>
            
            {/* Market Search Input */}
            {availableMarkets.length > 0 && (
              <TextInput
                style={styles.filterSearchInput}
                placeholder="Search markets..."
                placeholderTextColor="#6B7280"
                value={marketSearchQuery}
                onChangeText={setMarketSearchQuery}
              />
            )}
            
            <ScrollView 
              style={styles.filterTagsScrollView}
              contentContainerStyle={styles.filterTagsContainer}
              nestedScrollEnabled={true}
            >
              {filteredAvailableMarkets.length > 0 ? filteredAvailableMarkets.map(market => (
                <TouchableOpacity
                  key={market}
                  onPress={() => toggleMarketFilter(market)}
                  style={[
                    styles.filterTag,
                    filterMarkets.includes(market) && styles.filterTagActive
                  ]}
                >
                  <Text style={[
                    styles.filterTagText,
                    filterMarkets.includes(market) && styles.filterTagTextActive
                  ]}>
                    {market}
                  </Text>
                </TouchableOpacity>
              )) : <Text style={styles.noFiltersText}>
                {marketSearchQuery ? `No markets match "${marketSearchQuery}"` : 'No markets'}
              </Text>}
            </ScrollView>
          </View>
          
          {/* Sort Options */}
          <View style={styles.filterSection}>
            <Text style={styles.filterLabel}>Sort By</Text>
            <View style={styles.filterTagsContainer}>
              {[
                { value: 'ev', label: 'Best EV' },
                { value: 'date', label: 'Date' },
                { value: 'price_desc', label: 'Highest Odds' },
                { value: 'price_asc', label: 'Lowest Odds' },
              ].map(sort => (
                <TouchableOpacity
                  key={sort.value}
                  onPress={() => setSortBy(sort.value)}
                  style={[
                    styles.filterTag,
                    sortBy === sort.value && styles.filterTagActive
                  ]}
                >
                  <Text style={[
                    styles.filterTagText,
                    sortBy === sort.value && styles.filterTagTextActive
                  ]}>
                    {sort.label}
                  </Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
          
          {/* Clear All Filters */}
          {(filterSportsbooks.length > 0 || filterMarkets.length > 0 || searchQuery || sortBy !== 'ev') && (
            <TouchableOpacity onPress={clearAllFilters} style={styles.clearAllButton}>
              <Text style={styles.clearAllButtonText}>✕ Clear All Filters</Text>
            </TouchableOpacity>
          )}
        </View>
      )}
      
      {/* Loading State */}
      {loading && (
        <View style={styles.loadingContainer}>
          <LoadingSpinner size={32} color="#007AFF" />
          <Text style={styles.loadingText}>Loading fixtures...</Text>
        </View>
      )}
      
      {/* Fixtures List */}
      {!loading && (
        <ScrollView style={styles.fixturesContainer} showsVerticalScrollIndicator={false}>
          {filteredFixtures.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>
                {searchQuery 
                  ? `No fixtures found matching "${searchQuery}"`
                  : 'No cached fixtures available'}
              </Text>
            </View>
          ) : (
            <>
              <View style={styles.paginationInfo}>
                <Text style={styles.resultsCount}>
                  Showing {fixtures.length} of {totalFixtures} fixture{totalFixtures !== 1 ? 's' : ''}
                </Text>
                <Text style={styles.pageInfo}>
                  Page {currentPage} of {Math.ceil(totalFixtures / fixturesPerPage) || 1}
                </Text>
              </View>
              
              {/* Pagination Controls */}
              {totalFixtures > fixturesPerPage && (
                <View style={styles.paginationControls}>
                  <TouchableOpacity
                    style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
                    onPress={() => setCurrentPage(1)}
                    disabled={currentPage === 1}
                  >
                    <Text style={styles.paginationButtonText}>First</Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity
                    style={[styles.paginationButton, currentPage === 1 && styles.paginationButtonDisabled]}
                    onPress={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <Text style={styles.paginationButtonText}>← Prev</Text>
                  </TouchableOpacity>
                  
                  <Text style={styles.paginationPageText}>
                    Page {currentPage}
                  </Text>
                  
                  <TouchableOpacity
                    style={[styles.paginationButton, currentPage >= Math.ceil(totalFixtures / fixturesPerPage) && styles.paginationButtonDisabled]}
                    onPress={() => setCurrentPage(prev => Math.min(Math.ceil(totalFixtures / fixturesPerPage), prev + 1))}
                    disabled={currentPage >= Math.ceil(totalFixtures / fixturesPerPage)}
                  >
                    <Text style={styles.paginationButtonText}>Next →</Text>
                  </TouchableOpacity>
                  
                  <TouchableOpacity
                    style={[styles.paginationButton, currentPage >= Math.ceil(totalFixtures / fixturesPerPage) && styles.paginationButtonDisabled]}
                    onPress={() => setCurrentPage(Math.ceil(totalFixtures / fixturesPerPage))}
                    disabled={currentPage >= Math.ceil(totalFixtures / fixturesPerPage)}
                  >
                    <Text style={styles.paginationButtonText}>Last</Text>
                  </TouchableOpacity>
                </View>
              )}
              
              {filteredFixtures.map(fixture => {
                const docId = fixture.id;
                const isExpanded = expandedFixtures[docId];
                const gameStatus = getGameStatus(fixture.start_date);
                
                return (
                  <View 
                    key={docId} 
                    style={[
                      styles.fixtureCard,
                      { 
                        borderLeftWidth: 4, 
                        borderLeftColor: gameStatus.color || '#3B82F6' 
                      }
                    ]}
                  >
                    {/* Fixture Header (Always visible) */}
                    <TouchableOpacity 
                      style={styles.fixtureHeader}
                      onPress={() => toggleFixture(fixture)}
                    >
                      <View style={styles.fixtureHeaderLeft}>
                        <View style={styles.fixtureHeaderTop}>
                          <Text style={styles.fixtureLeague}>
                            {(fixture.league || '').toUpperCase()}
                          </Text>
                          <View style={styles.statusBadge}>
                            <Text style={styles.statusFlag}>{gameStatus.flag}</Text>
                            <Text style={[styles.statusLabel, { color: gameStatus.color }]}>
                              {gameStatus.label}
                            </Text>
                          </View>
                        </View>
                        <Text style={styles.fixtureTeams}>
                          {fixture.away_team} @ {fixture.home_team}
                        </Text>
                        <Text style={styles.fixtureDate}>
                          {formatDate(fixture.start_date)}
                        </Text>
                      </View>
                      
                      <View style={styles.fixtureHeaderRight}>
                        {(fixture.total_markets || fixture.markets_count) > 0 && (
                          <View style={styles.marketsBadge}>
                            <Text style={styles.marketsBadgeText}>
                              {fixture.total_markets || fixture.markets_count} markets
                            </Text>
                          </View>
                        )}
                        <Text style={styles.expandIcon}>
                          {isExpanded ? '▼' : '▶'}
                        </Text>
                      </View>
                    </TouchableOpacity>
                    
                    {/* Expanded Content: Markets & Odds */}
                    {isExpanded && (fixture.featured_markets || fixture.markets) && (
                      <View style={styles.fixtureDetails}>
                        {(() => {
                          // Use new structure (featured_markets) or fallback to legacy structure (markets)
                          const markets = fixture.featured_markets || fixture.markets || {};
                          
                          if (Object.keys(markets).length === 0) {
                            return <Text style={styles.noMarketsText}>No markets cached for this fixture</Text>;
                          }
                          
                          return Object.entries(markets).map(([marketKey, marketData]) => {
                            // ⭐ NEW: Apply player filter if player search is active
                            const filteredMarketData = playerSearch ? filterMarketDataByPlayer(marketData, playerSearch) : marketData;
                            
                            // Skip this market if player filter removed all odds
                            if (!filteredMarketData) return null;
                            
                            // IMPORTANT: Use docId (fixture.id) consistently for state keys
                            const marketExpanded = expandedMarkets[`${docId}_${marketKey}`];
                            const cacheKey = `${docId}_${marketKey}`;
                            const isLoadingFull = loadingMarkets[cacheKey];
                            
                            // Get odds data: use full data if loaded, otherwise use featured/cached data
                            const oddsData = fullMarketData[cacheKey] || filteredMarketData.data || [];
                            const hasMoreData = filteredMarketData.total_available && filteredMarketData.total_available > filteredMarketData.odds_count;
                            
                            return (
                              <View key={marketKey} style={styles.marketCard}>
                                {/* Market Header */}
                                <TouchableOpacity
                                  style={styles.marketHeader}
                                  onPress={() => {
                                    console.log('[CacheExplorer] Market clicked:', {
                                      marketKey,
                                      marketName: filteredMarketData.market_name,
                                      oddsDataLength: oddsData.length,
                                      marketData: {
                                        total_available: filteredMarketData.total_available,
                                        odds_count: filteredMarketData.odds_count,
                                        hasData: !!filteredMarketData.data,
                                        dataLength: filteredMarketData.data?.length
                                      },
                                      fixtureId: docId,
                                      isExpanded: marketExpanded,
                                      playerFilter: playerSearch
                                    });
                                    toggleMarket(docId, marketKey, fixture);
                                  }}
                                >
                                  <Text style={styles.marketName}>
                                    📊 {filteredMarketData.market_name}
                                  </Text>
                                  <View style={styles.marketHeaderRight}>
                                    <Text style={styles.oddsCount}>
                                      {filteredMarketData.total_available || oddsData.length} odds
                                      {hasMoreData && ' (load all)'}
                                    </Text>
                                    <Text style={styles.expandIcon}>
                                      {marketExpanded ? '▼' : '▶'}
                                    </Text>
                                  </View>
                                </TouchableOpacity>
                                
                                {/* Expanded: Actual Odds Data - Grouped by Selection */}
                                {marketExpanded && !isLoadingFull && oddsData.length > 0 && (() => {
                                  const groupedOdds = groupOddsBySelection(oddsData);
                                  const displayLimit = oddsDisplayLimit[cacheKey] || 200; // Increased from 50 to 200
                                  const displayedOdds = groupedOdds.slice(0, displayLimit);
                                  const hasMore = groupedOdds.length > displayLimit;
                                  
                                  return (
                                  <View style={styles.oddsContainer}>
                                    {displayedOdds.map((group, index) => (
                                      <View key={index} style={styles.selectionGroup}>
                                        {/* Selection Header */}
                                        <View style={styles.selectionHeader}>
                                          <Text style={styles.oddSelection}>
                                            {group.selection || 'Unknown'}
                                            {group.selection_line && ` (${group.selection_line})`}
                                          </Text>
                                          {group.player_id && (
                                            <Text style={styles.oddPlayer}>Player Prop</Text>
                                          )}
                                        </View>
                                        
                                        {/* All Sportsbooks for this selection */}
                                        <View style={styles.sportsbooksContainer}>
                                          {group.sportsbooks.map((book, bookIdx) => {
                                            const mathKey = `${docId}-${marketKey}-${group.selection}-${book.sportsbook}`;
                                            const showMath = expandedMath[mathKey];
                                            
                                            return (
                                              <View key={bookIdx} style={styles.sportsbookRowContainer}>
                                                <View style={styles.sportsbookRow}>
                                                  <Text style={styles.oddSportsbook}>{book.sportsbook}</Text>
                                                  <View style={styles.sportsbookOddsInfo}>
                                                    <Text style={[
                                                      styles.oddPrice,
                                                      book.price > 0 ? styles.oddPricePositive : styles.oddPriceNegative
                                                    ]}>
                                                      {book.price > 0 ? '+' : ''}{book.price}
                                                    </Text>
                                                    {book.ev_percent !== undefined && book.ev_percent !== null ? (
                                                      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                                        <Text style={[
                                                          styles.oddEV,
                                                          book.ev_percent > 0 ? styles.oddEVPositive : styles.oddEVNegative
                                                        ]}>
                                                          {book.ev_percent.toFixed(1)}%
                                                        </Text>
                                                        <TouchableOpacity 
                                                          onPress={() => setExpandedMath(prev => ({ ...prev, [mathKey]: !prev[mathKey] }))}
                                                          style={styles.mathToggleButton}
                                                        >
                                                          <Text style={styles.mathToggleText}>
                                                            {showMath ? '📉 Hide Math' : '📊 Show Math'}
                                                          </Text>
                                                        </TouchableOpacity>
                                                      </View>
                                                    ) : (
                                                      <Text style={styles.oddEVNull}>EV: N/A</Text>
                                                    )}
                                                  </View>
                                                </View>
                                                
                                                {/* Math Breakdown */}
                                                {showMath && book.devig_methods && (
                                                  <View style={styles.mathBreakdown}>
                                                    <Text style={styles.mathTitle}>🧮 EV% Calculation Breakdown</Text>
                                                    
                                                    {/* Decimal Odds */}
                                                    <View style={styles.mathSection}>
                                                      <Text style={styles.mathLabel}>American Odds:</Text>
                                                      <Text style={styles.mathValue}>
                                                        {book.price > 0 ? '+' : ''}{book.price}
                                                      </Text>
                                                    </View>
                                                    <View style={styles.mathSection}>
                                                      <Text style={styles.mathLabel}>Decimal Odds:</Text>
                                                      <Text style={styles.mathValue}>
                                                        {book.decimal_odds?.toFixed(4) || (book.price > 0 ? ((book.price / 100) + 1).toFixed(4) : ((100 / -book.price) + 1).toFixed(4))}
                                                      </Text>
                                                    </View>
                                                    
                                                    <View style={styles.mathDivider} />
                                                    
                                                    {/* EV% Comparison Table */}
                                                    <View style={styles.evComparisonTable}>
                                                      <Text style={styles.evComparisonTitle}>📊 All Three Methods - EV% Comparison</Text>
                                                      
                                                      <View style={styles.evComparisonRow}>
                                                        <View style={styles.evComparisonCell}>
                                                          <Text style={styles.evComparisonLabel}>Additive</Text>
                                                          <Text style={[
                                                            styles.evComparisonValue,
                                                            book.ev_method1_additive > 0 ? styles.oddEVPositive : styles.oddEVNegative,
                                                            book.ev_method_used === 'additive' && styles.evComparisonValueActive
                                                          ]}>
                                                            {book.ev_method1_additive?.toFixed(2) || 'N/A'}%
                                                          </Text>
                                                          {book.ev_method_used === 'additive' && (
                                                            <Text style={styles.evComparisonBadge}>✓ SHOWN</Text>
                                                          )}
                                                        </View>
                                                        
                                                        <View style={styles.evComparisonCell}>
                                                          <Text style={styles.evComparisonLabel}>Multiplicative</Text>
                                                          <Text style={[
                                                            styles.evComparisonValue,
                                                            book.ev_method2_multiplicative > 0 ? styles.oddEVPositive : styles.oddEVNegative,
                                                            book.ev_method_used === 'multiplicative' && styles.evComparisonValueActive
                                                          ]}>
                                                            {book.ev_method2_multiplicative?.toFixed(2) || 'N/A'}%
                                                          </Text>
                                                          {book.ev_method_used === 'multiplicative' && (
                                                            <Text style={styles.evComparisonBadge}>✓ SHOWN</Text>
                                                          )}
                                                        </View>
                                                        
                                                        {book.ev_method3_power !== undefined && (
                                                          <View style={styles.evComparisonCell}>
                                                            <Text style={styles.evComparisonLabel}>Power</Text>
                                                            <Text style={[
                                                              styles.evComparisonValue,
                                                              book.ev_method3_power > 0 ? styles.oddEVPositive : styles.oddEVNegative,
                                                              book.ev_method_used === 'power' && styles.evComparisonValueActive
                                                            ]}>
                                                              {book.ev_method3_power?.toFixed(2) || 'N/A'}%
                                                            </Text>
                                                            {book.ev_method_used === 'power' && (
                                                              <Text style={styles.evComparisonBadge}>✓ SHOWN</Text>
                                                            )}
                                                          </View>
                                                        )}
                                                      </View>
                                                      
                                                      <Text style={styles.evComparisonFootnote}>
                                                        We display the lowest (most conservative) EV% to users
                                                      </Text>
                                                    </View>
                                                    
                                                    <View style={styles.mathDivider} />
                                                    
                                                    {/* Method 1: Additive */}
                                                    <View style={[styles.mathMethodCard, book.ev_method_used === 'additive' && styles.mathMethodCardActive]}>
                                                      <Text style={styles.mathMethodTitle}>
                                                        Method 1: Additive (Conservative)
                                                        {book.ev_method_used === 'additive' && ' ✓ DISPLAYED'}
                                                      </Text>
                                                      <Text style={styles.mathFormula}>fairProb = impliedProb / totalImpliedProb</Text>
                                                      <Text style={styles.mathCalc}>
                                                        fairProb = {book.devig_methods.additive_prob.toFixed(6)} ({(book.devig_methods.additive_prob * 100).toFixed(2)}%)
                                                      </Text>
                                                      <Text style={styles.mathFormula}>EV% = (fairProb × decimalOdds – 1) × 100</Text>
                                                      <Text style={styles.mathResult}>
                                                        EV% = {book.ev_method1_additive?.toFixed(2) || 'N/A'}%
                                                      </Text>
                                                    </View>
                                                    
                                                    {/* Method 2: Multiplicative */}
                                                    <View style={[styles.mathMethodCard, book.ev_method_used === 'multiplicative' && styles.mathMethodCardActive]}>
                                                      <Text style={styles.mathMethodTitle}>
                                                        Method 2: Multiplicative (Industry Standard)
                                                        {book.ev_method_used === 'multiplicative' && ' ✓ DISPLAYED'}
                                                      </Text>
                                                      <Text style={styles.mathFormula}>fairProb = impliedProb / totalImpliedProb</Text>
                                                      <Text style={styles.mathCalc}>
                                                        fairProb = {book.devig_methods.multiplicative_prob.toFixed(6)} ({(book.devig_methods.multiplicative_prob * 100).toFixed(2)}%)
                                                      </Text>
                                                      <Text style={styles.mathFormula}>EV% = (fairProb × decimalOdds – 1) × 100</Text>
                                                      <Text style={styles.mathResult}>
                                                        EV% = {book.ev_method2_multiplicative?.toFixed(2) || 'N/A'}%
                                                      </Text>
                                                    </View>
                                                    
                                                    {/* Method 3: Power (Clarke et al.) */}
                                                    {book.devig_methods.power_prob && (
                                                      <View style={[styles.mathMethodCard, book.ev_method_used === 'power' && styles.mathMethodCardActive]}>
                                                        <Text style={styles.mathMethodTitle}>
                                                          Method 3: Power (Clarke et al.)
                                                          {book.ev_method_used === 'power' && ' ✓ DISPLAYED'}
                                                        </Text>
                                                        <Text style={styles.mathFormula}>fairProb = (impliedProb^k) / sum(allProbs^k)</Text>
                                                        <Text style={styles.mathCalc}>
                                                          fairProb = {book.devig_methods.power_prob.toFixed(6)} ({(book.devig_methods.power_prob * 100).toFixed(2)}%)
                                                        </Text>
                                                        <Text style={styles.mathFormula}>EV% = (fairProb × decimalOdds – 1) × 100</Text>
                                                        <Text style={styles.mathResult}>
                                                          EV% = {book.ev_method3_power?.toFixed(2) || 'N/A'}%
                                                        </Text>
                                                      </View>
                                                    )}
                                                    
                                                    {/* Final Result */}
                                                    <View style={styles.mathFinalResult}>
                                                      <View>
                                                        <Text style={styles.mathFinalLabel}>Displayed to User:</Text>
                                                        <Text style={styles.mathFinalSubtext}>
                                                          Math.min (Most Conservative)
                                                        </Text>
                                                      </View>
                                                      <Text style={[
                                                        styles.mathFinalValue,
                                                        book.ev_percent > 0 ? styles.oddEVPositive : styles.oddEVNegative
                                                      ]}>
                                                        {book.ev_percent.toFixed(2)}%
                                                      </Text>
                                                    </View>
                                                    
                                                    <Text style={styles.mathFootnote}>
                                                      📌 We calculate all three methods and show the lowest (most conservative) EV%
                                                    </Text>
                                                  </View>
                                                )}
                                              </View>
                                            );
                                          })}
                                        </View>
                                      </View>
                                    ))}
                                    
                                    {/* Load More Button */}
                                    {hasMore && (
                                      <TouchableOpacity
                                        style={styles.loadMoreButton}
                                        onPress={() => {
                                          setOddsDisplayLimit(prev => ({
                                            ...prev,
                                            [cacheKey]: (prev[cacheKey] || 200) + 100 // Load 100 more at a time
                                          }));
                                        }}
                                      >
                                        <Text style={styles.loadMoreText}>
                                          📊 Load More ({groupedOdds.length - displayLimit} more selections)
                                        </Text>
                                      </TouchableOpacity>
                                    )}
                                  </View>
                                  );
                                })()}
                                
                                {/* Loading full market data */}
                                {marketExpanded && isLoadingFull && (
                                  <View style={styles.oddsContainer}>
                                    <Text style={styles.loadingText}>Loading all odds...</Text>
                                  </View>
                                )}
                                
                                {/* No odds available */}
                                {marketExpanded && !isLoadingFull && oddsData.length === 0 && (
                                  <View style={styles.oddsContainer}>
                                    <Text style={styles.noOddsText}>No odds available for this market</Text>
                                  </View>
                                )}
                              </View>
                            );
                          });
                        })()}
                      </View>
                    )}
                  </View>
                );
              })}
            </>
          )}
        </ScrollView>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    marginBottom: 15,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#E5E7EB',
    marginBottom: 5,
  },
  headerSubtitle: {
    fontSize: 13,
    color: '#9CA3AF',
  },
  searchContainer: {
    marginBottom: 12,
  },
  searchInput: {
    height: 38,
    backgroundColor: '#1F2937',
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 14,
    color: '#E5E7EB',
    borderWidth: 1,
    borderColor: '#374151',
  },
  advancedSearchContainer: {
    flexDirection: 'row',
    gap: 10,
    marginBottom: 12,
  },
  advancedSearchInput: {
    flex: 1,
    height: 38,
    backgroundColor: '#1F2937',
    borderRadius: 8,
    paddingHorizontal: 12,
    fontSize: 13,
    color: '#E5E7EB',
    borderWidth: 1,
    borderColor: '#374151',
  },
  leagueFilterContainer: {
    height: 40,
    marginBottom: 15,
  },
  leagueFilter: {
    flexGrow: 0,
  },
  leagueFilterContent: {
    alignItems: 'center',
    paddingVertical: 4,
  },
  leagueButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: '#1F2937',
    borderRadius: 6,
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#374151',
    height: 32,
    justifyContent: 'center',
    alignItems: 'center',
  },
  leagueButtonActive: {
    backgroundColor: '#3B82F6',
    borderColor: '#3B82F6',
  },
  leagueButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#9CA3AF',
  },
  leagueButtonTextActive: {
    color: '#FFFFFF',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 30,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 14,
    color: '#9CA3AF',
  },
  fixturesContainer: {
    flex: 1,
  },
  resultsCount: {
    fontSize: 12,
    color: '#9CA3AF',
    fontWeight: '600',
  },
  paginationInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
    paddingHorizontal: 4,
  },
  pageInfo: {
    fontSize: 12,
    color: '#6B7280',
    fontWeight: '500',
  },
  paginationControls: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 8,
    marginBottom: 15,
    paddingVertical: 10,
    backgroundColor: '#1F2937',
    borderRadius: 8,
  },
  paginationButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#374151',
    borderRadius: 6,
    minWidth: 60,
    alignItems: 'center',
  },
  paginationButtonDisabled: {
    backgroundColor: '#1F2937',
    opacity: 0.5,
  },
  paginationButtonText: {
    color: '#fff',
    fontSize: 13,
    fontWeight: '600',
  },
  paginationPageText: {
    color: '#E5E7EB',
    fontSize: 13,
    fontWeight: '600',
    paddingHorizontal: 12,
  },
  emptyState: {
    padding: 30,
    alignItems: 'center',
  },
  emptyStateText: {
    fontSize: 14,
    color: '#6B7280',
    textAlign: 'center',
  },
  fixtureCard: {
    backgroundColor: '#1F2937',
    borderRadius: 8,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: '#374151',
    overflow: 'hidden',
  },
  fixtureHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 12,
  },
  fixtureHeaderLeft: {
    flex: 1,
  },
  fixtureHeaderTop: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 4,
  },
  fixtureLeague: {
    fontSize: 10,
    fontWeight: '700',
    color: '#3B82F6',
    marginRight: 10,
    textTransform: 'uppercase',
  },
  statusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#111827',
    paddingVertical: 2,
    paddingHorizontal: 6,
    borderRadius: 4,
  },
  statusFlag: {
    fontSize: 10,
    marginRight: 4,
  },
  statusLabel: {
    fontSize: 9,
    fontWeight: '700',
    textTransform: 'uppercase',
  },
  fixtureTeams: {
    fontSize: 14,
    fontWeight: '600',
    color: '#E5E7EB',
    marginBottom: 4,
  },
  fixtureDate: {
    fontSize: 11,
    color: '#9CA3AF',
  },
  fixtureHeaderRight: {
    alignItems: 'flex-end',
  },
  marketsBadge: {
    backgroundColor: '#1E3A8A',
    paddingVertical: 3,
    paddingHorizontal: 8,
    borderRadius: 4,
    marginBottom: 5,
  },
  marketsBadgeText: {
    fontSize: 10,
    color: '#93C5FD',
    fontWeight: '600',
  },
  expandIcon: {
    fontSize: 12,
    color: '#3B82F6',
    fontWeight: 'bold',
  },
  fixtureDetails: {
    borderTopWidth: 1,
    borderTopColor: '#374151',
    paddingTop: 8,
    paddingHorizontal: 12,
    paddingBottom: 12,
  },
  noMarketsText: {
    fontSize: 12,
    color: '#6B7280',
    fontStyle: 'italic',
    textAlign: 'center',
    paddingVertical: 15,
  },
  marketCard: {
    backgroundColor: '#111827',
    borderRadius: 6,
    marginBottom: 8,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#374151',
  },
  marketHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
  },
  marketName: {
    fontSize: 12,
    fontWeight: '600',
    color: '#E5E7EB',
    flex: 1,
  },
  marketHeaderRight: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  oddsCount: {
    fontSize: 10,
    color: '#9CA3AF',
    marginRight: 8,
  },
  oddsContainer: {
    paddingHorizontal: 10,
    paddingBottom: 10,
  },
  oddRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
    backgroundColor: '#fff',
    borderRadius: 4,
    marginBottom: 4,
  },
  oddLeft: {
    flex: 1,
    marginRight: 10,
  },
  oddSelection: {
    fontSize: 13,
    fontWeight: '600',
    color: '#E5E7EB',
  },
  oddPlayer: {
    fontSize: 9,
    color: '#9CA3AF',
    marginTop: 2,
  },
  oddRight: {
    alignItems: 'flex-end',
  },
  oddPrice: {
    fontSize: 14,
    fontWeight: 'bold',
    marginBottom: 1,
  },
  oddPricePositive: {
    color: '#10B981',
  },
  oddPriceNegative: {
    color: '#EF4444',
  },
  oddSportsbook: {
    fontSize: 10,
    color: '#9CA3AF',
    marginBottom: 1,
  },
  oddEV: {
    fontSize: 10,
    fontWeight: '600',
  },
  oddEVPositive: {
    color: '#10B981',
  },
  oddEVNegative: {
    color: '#EF4444',
  },
  oddEVNull: {
    fontSize: 10,
    color: '#6B7280',
    fontStyle: 'italic',
  },
  moreOddsText: {
    fontSize: 10,
    color: '#3B82F6',
    textAlign: 'center',
    marginTop: 8,
    fontStyle: 'italic',
  },
  loadMoreButton: {
    backgroundColor: '#1E3A8A',
    padding: 12,
    borderRadius: 8,
    marginTop: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#3B82F6',
  },
  loadMoreText: {
    color: '#60A5FA',
    fontSize: 13,
    fontWeight: '600',
  },
  loadingText: {
    fontSize: 11,
    color: '#9CA3AF',
    textAlign: 'center',
    padding: 12,
    fontStyle: 'italic',
  },
  noOddsText: {
    fontSize: 11,
    color: '#6B7280',
    textAlign: 'center',
    padding: 12,
    fontStyle: 'italic',
  },
  loadingDetails: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 15,
  },
  loadingDetailsText: {
    marginLeft: 8,
    fontSize: 12,
    color: '#9CA3AF',
  },
  // Filters styles
  filtersToggle: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#1F2937',
    padding: 10,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#374151',
    marginBottom: 12,
  },
  filtersToggleText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#E5E7EB',
  },
  filtersToggleIcon: {
    fontSize: 10,
    color: '#3B82F6',
  },
  filtersPanel: {
    backgroundColor: '#111827',
    borderRadius: 8,
    padding: 12,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#374151',
  },
  filterSection: {
    marginBottom: 12,
  },
  filterSectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 8,
  },
  filterLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: '#E5E7EB',
    textTransform: 'uppercase',
  },
  clearButton: {
    fontSize: 11,
    color: '#3B82F6',
    fontWeight: '600',
  },
  filterSearchInput: {
    height: 34,
    backgroundColor: '#1F2937',
    borderRadius: 6,
    paddingHorizontal: 10,
    fontSize: 12,
    color: '#E5E7EB',
    borderWidth: 1,
    borderColor: '#374151',
    marginBottom: 8,
  },
  filterTagsScrollView: {
    maxHeight: 180,
  },
  filterTagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  filterTag: {
    paddingVertical: 5,
    paddingHorizontal: 10,
    backgroundColor: '#1F2937',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#374151',
  },
  filterTagActive: {
    backgroundColor: '#3B82F6',
    borderColor: '#3B82F6',
  },
  filterTagText: {
    fontSize: 11,
    color: '#9CA3AF',
    fontWeight: '600',
  },
  filterTagTextActive: {
    color: '#FFFFFF',
  },
  noFiltersText: {
    fontSize: 11,
    color: '#6B7280',
    fontStyle: 'italic',
  },
  moreFiltersText: {
    fontSize: 10,
    color: '#9CA3AF',
    fontStyle: 'italic',
    paddingVertical: 5,
    paddingHorizontal: 10,
  },
  clearAllButton: {
    backgroundColor: '#EF4444',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 6,
    alignItems: 'center',
    marginTop: 4,
  },
  clearAllButtonText: {
    color: '#fff',
    fontSize: 12,
    fontWeight: '700',
  },
  // Grouped odds styles
  selectionGroup: {
    backgroundColor: '#1F2937',
    borderRadius: 6,
    marginBottom: 10,
    padding: 10,
    borderWidth: 1,
    borderColor: '#374151',
  },
  selectionHeader: {
    marginBottom: 8,
    paddingBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: '#374151',
  },
  sportsbooksContainer: {
    gap: 6,
  },
  sportsbookRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 6,
    paddingHorizontal: 8,
    backgroundColor: '#111827',
    borderRadius: 4,
  },
  sportsbookOddsInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  sportsbookRowContainer: {
    marginBottom: 8,
  },
  mathToggleButton: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#1F2937',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#374151',
  },
  mathToggleText: {
    fontSize: 11,
    color: '#3B82F6',
    fontWeight: '600',
  },
  mathBreakdown: {
    marginTop: 8,
    padding: 12,
    backgroundColor: '#0F1419',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#374151',
  },
  mathTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#E5E7EB',
    marginBottom: 12,
  },
  mathSection: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
    paddingVertical: 4,
  },
  mathLabel: {
    fontSize: 12,
    color: '#9CA3AF',
  },
  mathValue: {
    fontSize: 12,
    color: '#E5E7EB',
    fontWeight: '600',
  },
  mathMethodCard: {
    backgroundColor: '#1F2937',
    padding: 10,
    borderRadius: 6,
    marginBottom: 8,
    borderLeftWidth: 3,
    borderLeftColor: '#3B82F6',
  },
  mathMethodCardActive: {
    backgroundColor: '#1E3A5F',
    borderLeftColor: '#4ADE80',
    borderWidth: 1,
    borderColor: '#4ADE80',
  },
  mathDivider: {
    height: 1,
    backgroundColor: '#374151',
    marginVertical: 12,
  },
  mathMethodTitle: {
    fontSize: 12,
    fontWeight: '700',
    color: '#60A5FA',
    marginBottom: 6,
  },
  mathFormula: {
    fontSize: 11,
    color: '#9CA3AF',
    fontFamily: 'monospace',
    marginBottom: 4,
  },
  mathCalc: {
    fontSize: 11,
    color: '#D1D5DB',
    marginBottom: 4,
  },
  mathResult: {
    fontSize: 12,
    fontWeight: '700',
    color: '#4ADE80',
    marginTop: 4,
  },
  mathFinalResult: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 10,
    backgroundColor: '#374151',
    borderRadius: 6,
    marginTop: 8,
    marginBottom: 8,
  },
  mathFinalLabel: {
    fontSize: 12,
    fontWeight: '700',
    color: '#E5E7EB',
  },
  mathFinalSubtext: {
    fontSize: 10,
    color: '#9CA3AF',
    marginTop: 2,
  },
  mathFinalValue: {
    fontSize: 16,
    fontWeight: '700',
  },
  mathFootnote: {
    fontSize: 11,
    color: '#9CA3AF',
    fontStyle: 'italic',
    marginTop: 4,
  },
  evComparisonTable: {
    backgroundColor: '#1F2937',
    padding: 12,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: '#4ADE80',
    marginBottom: 8,
  },
  evComparisonTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: '#4ADE80',
    marginBottom: 12,
    textAlign: 'center',
  },
  evComparisonRow: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'flex-start',
    gap: 8,
  },
  evComparisonCell: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: '#0F1419',
    padding: 10,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#374151',
  },
  evComparisonLabel: {
    fontSize: 10,
    color: '#9CA3AF',
    marginBottom: 6,
    textAlign: 'center',
  },
  evComparisonValue: {
    fontSize: 18,
    fontWeight: '700',
    textAlign: 'center',
  },
  evComparisonValueActive: {
    fontSize: 20,
    textShadowColor: 'rgba(74, 222, 128, 0.5)',
    textShadowOffset: { width: 0, height: 0 },
    textShadowRadius: 8,
  },
  evComparisonBadge: {
    fontSize: 9,
    color: '#4ADE80',
    fontWeight: '700',
    marginTop: 4,
    textAlign: 'center',
  },
  evComparisonFootnote: {
    fontSize: 10,
    color: '#9CA3AF',
    fontStyle: 'italic',
    marginTop: 10,
    textAlign: 'center',
  },
});

export default CacheDataExplorer;

